import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { planService } from '../../application/PlanService';
import { PlanActionType } from '../../context/plan/actions';
import { usePlanContext } from '../../context/plan/context';
import { PlanFlat } from '../../domain/plan/PlanFlat';
import { Project } from '../../domain/project/Project';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';

import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

interface UseRetrieveFlatPlansProps {
  project?: Project;
}

type UseRetrieveFlatPlansResponse = ServiceResponse<Function, Array<PlanFlat>>;

// Hooks act as 'Adapter' layer.
export const useRetrieveFlatPlans = ({ project }: UseRetrieveFlatPlansProps): UseRetrieveFlatPlansResponse => {
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { planState, planDispatch } = usePlanContext();
  const { sessionExpiredLogout } = useErrorMiddleware();

  const {
    isInitialLoading: isLoading,
    refetch,
    isError,
    isSuccess,
  } = useQuery(
    [`planner_search_plans`, planState.predicatesBase64],
    (): Promise<Array<PlanFlat>> => {
      return planService.fetchFlatPlans({
        project: project!,
        predicates: planState.predicates,
      });
    },
    {
      retry: false,
      enabled: planState.predicatesBase64 !== `` && planState.predicates.length > 0,
      staleTime: Infinity,
      onSuccess: (flatPlans) => {
        planDispatch({
          type: PlanActionType.SEARCH_FLAT_PLANS_SUCCESS,
          payload: {
            flatPlans,
          },
        });
      },
      onError: (err: any) => {
        console.info(`Planner Retrieve Flat Plans Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  useEffect(() => {
    if (planState.predicates.length > 0) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planState.predicates]);

  return {
    error: isError,
    isLoading,
    success: isSuccess,
    run: refetch,
    data: planState.flatPlans ?? [],
  };
};
